import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    Alert,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { useJednotkaQuery } from '../api/JsdhNotifikaceService/service/AdminMutations';
import { useForgotPasswordMutation } from '../api/JsdhNotifikaceService/service/AuthMutation';
import AppearanceMenu from '../components/AppearanceMenu';

const ForgotPasswordPage: React.FC = () => {
    const { data: jednotka } = useJednotkaQuery();
    const forgotPasswordMutation = useForgotPasswordMutation();

    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError('Zadejte emailovou adresu.');
            return;
        }

        forgotPasswordMutation.mutate(
            { email: email.trim() },
            {
                onSuccess: () => {
                    setSubmitted(true);
                },
                onError: (err) => {
                    const statusCode = (err as any)?.statusCode;
                    if (statusCode === 429) {
                        setError('Příliš mnoho pokusů. Zkuste to později.');
                    } else if (statusCode === 503) {
                        setError('Email se nepodařilo odeslat. Zkuste to později nebo kontaktujte správce.');
                    } else {
                        setError('Nepodařilo se odeslat požadavek. Zkuste to později.');
                    }
                },
            }
        );
    };

    return (
        <Container maxWidth="xs" sx={{ mt: 8 }}>
            <Box
                sx={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    zIndex: 1000,
                }}
            >
                <AppearanceMenu />
            </Box>

            <Paper elevation={3} sx={{ p: 4 }}>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    mb={3}
                >
                    <Typography variant="h5" component="h1" gutterBottom>
                        Zapomenuté heslo
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {jednotka?.nazev || 'Hasiči Online'}
                    </Typography>
                </Box>

                {submitted ? (
                    <Box textAlign="center">
                        <CheckCircleOutline
                            color="success"
                            sx={{ fontSize: 64, mb: 2 }}
                        />
                        <Typography variant="h6" gutterBottom>
                            Požadavek odeslán
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Pokud je email registrován, obdržíte odkaz na reset hesla.
                            Zkontrolujte svou emailovou schránku.
                        </Typography>
                        <Button
                            component={Link}
                            to="/login"
                            variant="contained"
                            fullWidth
                        >
                            Zpět na přihlášení
                        </Button>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Zadejte svůj email a my vám zašleme odkaz na nastavení nového hesla.
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            variant="outlined"
                            margin="normal"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={forgotPasswordMutation.isPending}
                            autoComplete="email"
                            autoFocus
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={forgotPasswordMutation.isPending || !email.trim()}
                            sx={{ mt: 3, mb: 2, height: 48 }}
                        >
                            {forgotPasswordMutation.isPending ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Odeslat odkaz'
                            )}
                        </Button>

                        <Box textAlign="center">
                            <Typography
                                variant="body2"
                                component={Link}
                                to="/login"
                                sx={{
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Zpět na přihlášení
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default ForgotPasswordPage;
